import React from "react";
import { WizardStep } from "../types";
import "./WizardStepIndicator.css";

interface WizardStepIndicatorProps {
  currentStep: WizardStep;
  onStepClick: (step: WizardStep) => void;
  clientName: string;
  clientDob?: string | null;
  onExit: () => void;
}

const steps = [
  { step: WizardStep.UPLOAD_DOCUMENTS, label: "Upload Documents", icon: "📤" },
  { step: WizardStep.PATIENT_DATA, label: "Patient Data", icon: "👤" },
  { step: WizardStep.ASSESSMENT_RESULTS, label: "Assessment Results", icon: "📊" },
  { step: WizardStep.TREATMENT_GOALS, label: "Treatment Goals", icon: "🎯" },
  { step: WizardStep.SUPPORT_PLAN, label: "Support Plan", icon: "📋" },
  { step: WizardStep.REVIEW, label: "Review", icon: "✓" },
];

const WizardStepIndicator: React.FC<WizardStepIndicatorProps> = ({
  currentStep,
  onStepClick,
  clientName,
  clientDob,
  onExit,
}) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };
  const getStepStatus = (step: WizardStep): "completed" | "active" | "upcoming" => {
    if (step < currentStep) return "completed";
    if (step === currentStep) return "active";
    return "upcoming";
  };

  const canNavigateToStep = (step: WizardStep): boolean => {
    // Allow navigating to any completed step, current step, or the next step
    return step <= currentStep + 1;
  };

  return (
    <div className="wizard-step-indicator">
      <div className="wizard-header-info">
        <span className="client-icon">👤</span>
        <div className="client-details">
          <span className="client-name">{clientName}</span>
          {clientDob && (
            <span className="client-dob">DOB: {formatDate(clientDob)}</span>
          )}
        </div>
      </div>

      <div className="steps-container">
        {steps.map((stepInfo, index) => {
          const status = getStepStatus(stepInfo.step);
          const canNavigate = canNavigateToStep(stepInfo.step);

          return (
            <React.Fragment key={stepInfo.step}>
              <button
                className={`step-item step-item--${status} ${canNavigate ? "step-item--clickable" : ""}`}
                onClick={() => canNavigate && onStepClick(stepInfo.step)}
                disabled={!canNavigate}
                aria-current={status === "active" ? "step" : undefined}
              >
                <div className="step-number">
                  {status === "completed" ? (
                    <span className="step-check">✓</span>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <div className="step-info">
                  <span className="step-label">{stepInfo.label}</span>
                </div>
              </button>

              {index < steps.length - 1 && (
                <div
                  className={`step-connector ${
                    stepInfo.step < currentStep ? "step-connector--completed" : ""
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <button className="btn btn-outline exit-btn" onClick={onExit}>
        Exit
      </button>
    </div>
  );
};

export default WizardStepIndicator;
